import { Text } from '../../common/components';
import { Colors } from '../../common/constants/Colors';
import { t } from '../../common/i18n';
import { Deadline, DeadlineStatusKind } from '../types';
import { getStatusKind } from '../utils/deadlineStatus';
import CheckCircleIcon from './CheckCircleIcon';

const namespace = 'Deadlines';

const icons: Record<DeadlineStatusKind, JSX.Element> = {
  overdue: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path
        d="M12 3l9 16H3L12 3z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M12 10v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="16.5" r="1" fill="currentColor" />
    </svg>
  ),
  soon: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3.5 9h17M8 3.5v3M16 3.5v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  upcoming: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  completed: <CheckCircleIcon size={22} />,
};

interface DeadlineSummaryProps {
  deadlines: Deadline[];
}

const tiles: { kind: DeadlineStatusKind; color: Colors }[] = [
  { kind: 'overdue', color: 'SystemRed' },
  { kind: 'soon', color: 'SystemOrange' },
  { kind: 'upcoming', color: 'Neutral700' },
  { kind: 'completed', color: 'SystemGreen' },
];

export const DeadlineSummary: React.FC<DeadlineSummaryProps> = ({ deadlines }) => {
  const counts = deadlines.reduce<Record<DeadlineStatusKind, number>>(
    (acc, deadline) => {
      acc[getStatusKind(deadline)] += 1;
      return acc;
    },
    { overdue: 0, soon: 0, upcoming: 0, completed: 0 }
  );

  return (
    <div className="DeadlineSummary">
      {tiles.map(({ kind, color }) => (
        <div key={kind} className={`DeadlineSummary__tile DeadlineSummary__tile--${kind}`}>
          <span className="DeadlineSummary__chip">{icons[kind]}</span>
          <div className="DeadlineSummary__data">
            <Text as="div" size="body-small" weight="600" color={color}>
              {t(`summary.${kind}`, namespace)}
            </Text>
            <Text as="div" size="h2" weight="700" color="Neutral800">
              {counts[kind]}
            </Text>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DeadlineSummary;
